<script setup lang="ts">
import type { FaqItem } from '~/features/my-wallet/types/faq'
import { SolAccordion, SolInputText } from '@solfacil/girassol'
import { useI18n } from 'vue-i18n'

import IconSearch from '~icons/material-symbols/search'
import FaqRichText from './FaqRichText.vue'

interface Props {
  id: string
  title: string
  items: FaqItem[]
}

const props = defineProps<Props>()

const { t } = useI18n()

const searchText = ref('')

function normalizeForSearch(text: string) {
  return text.toLowerCase().normalize('NFD').replace(/\p{M}/gu, '')
}

const searchableTexts = computed(() => props.items.map(faqItem => normalizeForSearch(faqItem.searchText)))

const searchTerms = computed(() => normalizeForSearch(searchText.value.trim()).split(/\s+/).filter(Boolean))
const isSearching = computed(() => searchTerms.value.length > 0)

const filteredItems = computed(() => {
  if (!isSearching.value)
    return props.items

  return props.items.filter((_, index) => searchTerms.value.every(term => searchableTexts.value[index].includes(term)))
})

const resultsText = computed(() => isSearching.value
  ? t('my_wallet.faq.results', { shown: filteredItems.value.length, total: props.items.length })
  : '')

const searchBox = useTemplateRef<HTMLElement>('searchBox')
const rootElement = useTemplateRef<HTMLElement>('rootElement')

defineExpose({ rootElement })

function clearSearch() {
  searchText.value = ''
  searchBox.value?.querySelector('input')?.focus()
}
</script>

<template>
  <section :id="id" ref="rootElement" class="faq-accordion" tabindex="-1" :aria-labelledby="`${id}-title`">
    <div class="faq-accordion-header">
      <h3 :id="`${id}-title`" class="faq-accordion-title">
        {{ title }}
      </h3>
      <div ref="searchBox" class="faq-accordion-search">
        <SolInputText
          :id="`${id}-search`"
          v-model="searchText"
          :name="`${id}-search`"
          class="faq-accordion-search-input"
          type="search"
          :placeholder="t('my_wallet.faq.search_placeholder')"
          :aria-label="t('my_wallet.faq.search_placeholder')"
        >
          <template #icon:left>
            <IconSearch aria-hidden="true" />
          </template>
        </SolInputText>
        <button
          v-if="isSearching"
          :id="`${id}-clear`"
          type="button"
          class="faq-accordion-clear"
          @click="clearSearch"
        >
          {{ t('my_wallet.faq.clear') }}
        </button>
      </div>
      <p class="sr-only" role="status">
        {{ resultsText }}
      </p>
      <p v-if="isSearching" class="faq-accordion-results">
        <span aria-hidden="true">{{ resultsText }} </span>
        <button
          :id="`${id}-show-all`"
          type="button"
          class="faq-accordion-show-all"
          @click="clearSearch"
        >
          {{ t('my_wallet.faq.show_all') }}
        </button>
      </p>
    </div>

    <div v-if="filteredItems.length === 0" class="faq-accordion-empty">
      <p class="faq-accordion-empty-title">
        {{ t('my_wallet.faq.empty_title') }}
      </p>
      <p class="faq-accordion-empty-text">
        {{ t('my_wallet.faq.empty_text') }}
      </p>
    </div>

    <div v-else class="faq-accordion-list">
      <SolAccordion
        v-for="(faqItem, index) in filteredItems"
        :id="`${id}-${faqItem.key}`"
        :key="`${isSearching ? 'search' : 'all'}-${faqItem.key}`"
        :title="faqItem.question"
        :open="isSearching || index === 0"
        class="faq-accordion-item"
      >
        <div class="faq-accordion-answer">
          <template v-for="(answerBlock, blockIndex) in faqItem.answer" :key="blockIndex">
            <p v-if="answerBlock.kind === 'paragraph'">
              <FaqRichText :text="answerBlock.text" />
            </p>
            <component
              :is="answerBlock.kind === 'numbered' ? 'ol' : 'ul'"
              v-else
              class="faq-accordion-answer-list"
              :class="`-${answerBlock.kind}`"
            >
              <li v-for="(listEntry, listEntryIndex) in answerBlock.items" :key="listEntryIndex">
                <FaqRichText :text="listEntry" />
              </li>
            </component>
          </template>
        </div>
      </SolAccordion>
    </div>
  </section>
</template>

<style scoped lang="scss">
@use '../../styles/typography';

.faq-accordion {
  @apply flex flex-col gap-6 w-full p-4 rounded-lg;
  @apply bg-neutral-high-pure border border-neutral-high-medium;

  @screen sm:system {
    @apply p-6;
  }

  &:focus {
    @apply outline-none;
  }

  .faq-accordion-header {
    @apply flex flex-col gap-3;
  }

  .faq-accordion-title {
    @apply text-neutral-low-dark;
    @include typography.title-20(1.5);
  }

  .faq-accordion-search {
    @apply flex flex-wrap items-center gap-3;
  }

  .faq-accordion-search-input {
    @apply flex-1;
    min-width: 200px;

    :deep(.sol-input-core.-left-icon) {
      @apply pl-lg;
    }
  }

  .faq-accordion-clear {
    @apply h-12 px-4 rounded-md border border-neutral-low-dark;
    @apply fonts-body-medium-bold text-neutral-low-dark whitespace-nowrap cursor-pointer;
  }

  .faq-accordion-results {
    @apply fonts-body-medium-regular text-neutral-low-medium;
  }

  .faq-accordion-show-all {
    @apply fonts-body-medium-bold text-neutral-low-dark underline cursor-pointer;
  }

  .faq-accordion-empty {
    @apply flex flex-col gap-1 py-6 text-center;
  }

  .faq-accordion-empty-title {
    @apply fonts-body-large-bold text-neutral-low-dark;
  }

  .faq-accordion-empty-text {
    @apply fonts-body-medium-regular text-neutral-low-medium;
  }

  .faq-accordion-list {
    @apply flex flex-col w-full;
  }

  .faq-accordion-item {
    :deep(.summary > .icon) {
      @apply text-neutral-low-dark;
    }

    :deep(.accordion-content) {
      @apply px-4 pt-0 pb-4 -mt-2 border-b-0 bg-neutral-high-pure;
    }

    &[open] {
      @apply border-b;

      :deep(.summary) {
        @apply bg-neutral-high-pure;
      }
    }
  }

  .faq-accordion-answer {
    @apply flex flex-col gap-2 fonts-body-medium-regular text-neutral-low-medium;
    line-height: 1.6;
  }

  .faq-accordion-answer-list {
    @apply flex flex-col gap-1 pl-5;

    &.-bullets {
      @apply list-disc;
    }

    &.-numbered {
      @apply list-decimal;
    }
  }
}
</style>
